/**
 * FamilyModule -- Full family management UI.
 *
 * Sections:
 * A. Family Overview -- grid of member cards
 * B. Add Member Flow -- step-by-step modal
 * C. Member Detail View -- activity, controls, actions
 * D. Pending Invites -- active links with revoke
 *
 * Accessible from Settings or Community via #/more/family
 */

import type { AweniContext, AweniModule, Lang } from '../../types';
import {
  FamilyService,
  type FamilyMember,
  type InviteLink,
  type FamilyOverview,
  type CreateMemberPayload,
  type MemberActivity,
} from '../../services/FamilyService';

// ---------- Module ----------

export default class FamilyModule implements AweniModule {
  private ctx!: AweniContext;
  private container!: HTMLElement;
  private familyService!: FamilyService;
  private family: FamilyOverview | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.ctx = ctx;
    this.container = container;
    this.familyService = FamilyService.init(ctx.api);
    await this.loadAndRender();
  }

  unmount(): void {
    this.container.innerHTML = '';
  }

  private t(key: string, params?: Record<string, string | number>): string {
    return this.ctx.i18n.t(key, params);
  }

  private get lang(): Lang {
    return this.ctx.i18n.locale;
  }

  private get isRTL(): boolean {
    return this.ctx.i18n.dir === 'rtl';
  }

  // ---- Data Loading ----

  private async loadAndRender(): Promise<void> {
    this.container.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;padding:40px;"><div class="aweni-spinner"></div></div>`;
    try {
      this.family = await this.familyService.getFamily();
      this.renderOverview();
    } catch {
      this.renderEmpty();
    }
  }

  // ---- A. Family Overview ----

  private renderOverview(): void {
    const f = this.family;
    if (!f) { this.renderEmpty(); return; }

    const dir = this.isRTL ? 'rtl' : 'ltr';
    this.container.innerHTML = '';
    this.container.setAttribute('dir', dir);

    const wrapper = this.el('div', {
      style: 'max-width:640px;margin:0 auto;padding:16px;'
    });

    // Header
    const header = this.el('div', {
      style: 'display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;'
    });
    const titleEl = this.el('div');
    const h1 = this.el('h1', {
      style: 'font-size:22px;font-weight:700;color:var(--text-primary);margin:0;'
    });
    h1.textContent = f.familyName || this.t('family_title');
    const subtitle = this.el('p', {
      style: 'font-size:13px;color:var(--text-secondary);margin:4px 0 0;'
    });
    subtitle.textContent = this.t('family_member_count', { count: f.members.length });
    titleEl.append(h1, subtitle);
    header.appendChild(titleEl);

    // Add Member FAB
    const addBtn = this.el('button', {
      style: 'width:44px;height:44px;border-radius:50%;background:#0A0A0A;color:#fff;border:none;cursor:pointer;font-size:24px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(10, 10, 10,0.3);',
      'aria-label': this.t('family_add_member')
    });
    addBtn.textContent = '+';
    addBtn.addEventListener('click', () => this.showAddMemberModal());
    header.appendChild(addBtn);
    wrapper.appendChild(header);

    // Members grid
    const grid = this.el('div', {
      style: 'display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:12px;margin-bottom:24px;'
    });

    for (const member of f.members) {
      grid.appendChild(this.renderMemberCard(member));
    }
    wrapper.appendChild(grid);

    // Pending Invites section
    if (f.pendingInvites.length > 0) {
      const invitesHeader = this.el('h2', {
        style: 'font-size:16px;font-weight:600;color:var(--text-primary);margin:0 0 12px;'
      });
      invitesHeader.textContent = this.t('family_pending_invites');
      wrapper.appendChild(invitesHeader);

      const invitesList = this.el('div', { style: 'display:flex;flex-direction:column;gap:8px;' });
      for (const invite of f.pendingInvites) {
        invitesList.appendChild(this.renderInviteRow(invite));
      }
      wrapper.appendChild(invitesList);
    }

    this.container.appendChild(wrapper);
  }

  private renderMemberCard(member: FamilyMember): HTMLElement {
    const card = this.el('div', {
      style: 'background:var(--bg-secondary);border-radius:16px;padding:16px;cursor:pointer;transition:transform 0.15s,box-shadow 0.15s;text-align:center;position:relative;',
      role: 'button',
      tabindex: '0',
      'aria-label': `${member.name} - ${this.t('family_role_' + member.role)}`
    });
    card.addEventListener('mouseenter', () => { card.style.transform = 'translateY(-2px)'; card.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; });
    card.addEventListener('mouseleave', () => { card.style.transform = ''; card.style.boxShadow = ''; });
    card.addEventListener('click', () => this.showMemberDetail(member));
    card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.showMemberDetail(member); } });

    // Status dot
    const statusDot = this.el('div', {
      style: `position:absolute;top:12px;${this.isRTL ? 'left' : 'right'}:12px;width:8px;height:8px;border-radius:50%;background:${member.status === 'active' ? '#0A0A0A' : member.status === 'pending' ? '#F59E0B' : '#EF4444'};`
    });
    card.appendChild(statusDot);

    // Avatar
    const avatar = this.el('div', {
      style: 'width:56px;height:56px;border-radius:50%;background:linear-gradient(135deg,#0A0A0A,#3B82F6);color:#fff;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:700;margin:0 auto 8px;'
    });
    avatar.textContent = member.avatar || member.name.charAt(0).toUpperCase();
    card.appendChild(avatar);

    // Name
    const name = this.el('div', {
      style: 'font-size:14px;font-weight:600;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;'
    });
    name.textContent = member.name;
    card.appendChild(name);

    // Role badge
    const roleBadge = this.el('span', {
      style: `display:inline-block;margin-top:4px;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:500;${this.getRoleBadgeStyle(member.role)}`
    });
    roleBadge.textContent = this.t('family_role_' + member.role);
    card.appendChild(roleBadge);

    // Last active
    if (member.lastActive) {
      const lastActive = this.el('div', {
        style: 'font-size:11px;color:var(--text-secondary);margin-top:4px;'
      });
      lastActive.textContent = this.formatRelativeTime(member.lastActive);
      card.appendChild(lastActive);
    }

    return card;
  }

  private getRoleBadgeStyle(role: string): string {
    switch (role) {
      case 'admin': return 'background:#dbeafe;color:#1d4ed8;';
      case 'co-admin': return 'background:#e0e7ff;color:#4338ca;';
      case 'child': return 'background:#fef3c7;color:#92400e;';
      default: return 'background:#f1f5f9;color:#475569;';
    }
  }

  private renderInviteRow(invite: InviteLink): HTMLElement {
    const row = this.el('div', {
      style: 'display:flex;align-items:center;justify-content:space-between;background:var(--bg-secondary);border-radius:12px;padding:12px 16px;'
    });

    const info = this.el('div');
    const codeLine = this.el('div', { style: 'font-size:13px;font-weight:600;color:var(--text-primary);font-family:monospace;' });
    codeLine.textContent = invite.code;
    const meta = this.el('div', { style: 'font-size:11px;color:var(--text-secondary);margin-top:2px;' });
    meta.textContent = `${this.t('family_role_' + invite.type)} | ${this.t('family_used_count', { used: invite.usedCount, max: invite.maxUses })} | ${this.t('family_expires')} ${new Date(invite.expiresAt).toLocaleDateString()}`;
    info.append(codeLine, meta);

    const revokeBtn = this.el('button', {
      style: 'padding:6px 12px;background:#FEE2E2;color:#DC2626;border:none;border-radius:8px;font-size:12px;cursor:pointer;font-weight:500;'
    });
    revokeBtn.textContent = this.t('family_revoke');
    revokeBtn.addEventListener('click', async () => {
      try {
        await this.familyService.revokeInvite(invite.code);
        this.ctx.showNotification(this.t('family_invite_revoked'), 'success');
        await this.loadAndRender();
      } catch {
        this.ctx.showNotification(this.t('common_error'), 'error');
      }
    });

    row.append(info, revokeBtn);
    return row;
  }

  // ---- B. Add Member Flow ----

  private showAddMemberModal(): void {
    const overlay = this.createOverlay();
    const modal = this.el('div', {
      style: 'background:var(--bg-primary);border-radius:20px 20px 0 0;width:100%;max-width:480px;max-height:85vh;overflow-y:auto;padding:24px 20px 32px;position:relative;',
      role: 'dialog',
      'aria-label': this.t('family_add_member')
    });

    let step = 1;
    let role: 'child' | 'member' | 'co-admin' = 'child';
    let memberName = '';
    let memberAge = '';
    let memberEmail = '';
    let enabledModules = this.familyService.getDefaultEnabledModules();
    let parentalControls = true;

    const renderStep = () => {
      modal.innerHTML = '';

      // Close button
      const closeBtn = this.el('button', {
        style: `position:absolute;top:16px;${this.isRTL ? 'left' : 'right'}:16px;width:32px;height:32px;border-radius:50%;background:var(--bg-secondary);border:none;cursor:pointer;font-size:18px;color:var(--text-secondary);display:flex;align-items:center;justify-content:center;`,
        'aria-label': this.t('common_close')
      });
      closeBtn.textContent = '\u00d7';
      closeBtn.addEventListener('click', () => overlay.remove());
      modal.appendChild(closeBtn);

      // Step indicator
      const stepIndicator = this.el('div', { style: 'display:flex;gap:6px;margin-bottom:20px;' });
      for (let i = 1; i <= 4; i++) {
        const dot = this.el('div', {
          style: `width:${i === step ? '24px' : '8px'};height:8px;border-radius:4px;background:${i <= step ? '#0A0A0A' : 'var(--bg-secondary)'};transition:all 0.2s;`
        });
        stepIndicator.appendChild(dot);
      }
      modal.appendChild(stepIndicator);

      if (step === 1) {
        // Step 1: Role selection
        const title = this.el('h2', { style: 'font-size:20px;font-weight:700;margin:0 0 8px;color:var(--text-primary);' });
        title.textContent = this.t('family_step_role');
        const desc = this.el('p', { style: 'font-size:13px;color:var(--text-secondary);margin:0 0 20px;' });
        desc.textContent = this.t('family_step_role_desc');
        modal.append(title, desc);

        const roles: Array<{ id: 'child' | 'member' | 'co-admin'; icon: string; desc: string }> = [
          { id: 'child', icon: '\ud83d\udc67', desc: this.t('family_role_child_desc') },
          { id: 'member', icon: '\ud83d\udc64', desc: this.t('family_role_member_desc') },
          { id: 'co-admin', icon: '\ud83d\udc65', desc: this.t('family_role_coadmin_desc') },
        ];

        const roleList = this.el('div', { style: 'display:flex;flex-direction:column;gap:10px;' });
        for (const r of roles) {
          const btn = this.el('button', {
            style: `display:flex;align-items:center;gap:12px;width:100%;padding:14px 16px;border-radius:14px;border:2px solid ${role === r.id ? '#0A0A0A' : 'var(--bg-secondary)'};background:${role === r.id ? '#f0fdf4' : 'var(--bg-secondary)'};cursor:pointer;text-align:${this.isRTL ? 'right' : 'left'};transition:all 0.15s;font-family:inherit;`
          });
          const icon = this.el('span', { style: 'font-size:28px;' });
          icon.textContent = r.icon;
          const textWrap = this.el('div');
          const roleName = this.el('div', { style: 'font-size:15px;font-weight:600;color:var(--text-primary);' });
          roleName.textContent = this.t('family_role_' + r.id);
          const roleDesc = this.el('div', { style: 'font-size:12px;color:var(--text-secondary);margin-top:2px;' });
          roleDesc.textContent = r.desc;
          textWrap.append(roleName, roleDesc);
          btn.append(icon, textWrap);
          btn.addEventListener('click', () => {
            role = r.id;
            parentalControls = role === 'child';
            renderStep();
          });
          roleList.appendChild(btn);
        }
        modal.appendChild(roleList);

        // Next button
        const next = this.createPrimaryButton(this.t('common_next'), () => { step = 2; renderStep(); });
        next.style.marginTop = '20px';
        modal.appendChild(next);

      } else if (step === 2) {
        // Step 2: Details
        const title = this.el('h2', { style: 'font-size:20px;font-weight:700;margin:0 0 16px;color:var(--text-primary);' });
        title.textContent = this.t('family_step_details');
        modal.appendChild(title);

        const form = this.el('div', { style: 'display:flex;flex-direction:column;gap:14px;' });

        // Name
        form.appendChild(this.createInput(this.t('family_field_name'), memberName, (v) => { memberName = v; }));

        // Age (for children)
        if (role === 'child') {
          form.appendChild(this.createInput(this.t('family_field_age'), memberAge, (v) => { memberAge = v; }, 'number'));
        }

        // Email (optional for children)
        const emailLabel = role === 'child' ? `${this.t('family_field_email')} (${this.t('family_optional')})` : this.t('family_field_email');
        form.appendChild(this.createInput(emailLabel, memberEmail, (v) => { memberEmail = v; }, 'email'));

        modal.appendChild(form);

        const nav = this.el('div', { style: 'display:flex;gap:8px;margin-top:20px;' });
        nav.appendChild(this.createSecondaryButton(this.t('family_back'), () => { step = 1; renderStep(); }));
        nav.appendChild(this.createPrimaryButton(this.t('common_next'), () => {
          if (!memberName.trim()) { this.ctx.showNotification(this.t('family_name_required'), 'warning'); return; }
          step = 3;
          renderStep();
        }));
        modal.appendChild(nav);

      } else if (step === 3) {
        // Step 3: Module settings
        const title = this.el('h2', { style: 'font-size:20px;font-weight:700;margin:0 0 8px;color:var(--text-primary);' });
        title.textContent = this.t('family_step_settings');
        const desc = this.el('p', { style: 'font-size:13px;color:var(--text-secondary);margin:0 0 16px;' });
        desc.textContent = this.t('family_step_settings_desc');
        modal.append(title, desc);

        const allMods = this.familyService.getAllModules();
        const toggleList = this.el('div', { style: 'display:flex;flex-direction:column;gap:8px;' });

        for (const modId of allMods) {
          if (modId === 'home') continue; // Home is always enabled
          const row = this.el('div', {
            style: 'display:flex;align-items:center;justify-content:space-between;padding:10px 12px;background:var(--bg-secondary);border-radius:10px;'
          });
          const label = this.el('span', { style: 'font-size:14px;color:var(--text-primary);text-transform:capitalize;' });
          label.textContent = modId;
          const toggle = this.createToggle(enabledModules.includes(modId), (on) => {
            if (on && !enabledModules.includes(modId)) enabledModules.push(modId);
            else enabledModules = enabledModules.filter(m => m !== modId);
          });
          row.append(label, toggle);
          toggleList.appendChild(row);
        }
        modal.appendChild(toggleList);

        // Parental controls toggle
        if (role === 'child') {
          const pcRow = this.el('div', {
            style: 'display:flex;align-items:center;justify-content:space-between;padding:12px;background:#fef3c7;border-radius:10px;margin-top:12px;'
          });
          const pcLabel = this.el('span', { style: 'font-size:14px;font-weight:500;color:#92400e;' });
          pcLabel.textContent = this.t('family_parental_controls');
          const pcToggle = this.createToggle(parentalControls, (on) => { parentalControls = on; });
          pcRow.append(pcLabel, pcToggle);
          modal.appendChild(pcRow);
        }

        const nav = this.el('div', { style: 'display:flex;gap:8px;margin-top:20px;' });
        nav.appendChild(this.createSecondaryButton(this.t('family_back'), () => { step = 2; renderStep(); }));
        nav.appendChild(this.createPrimaryButton(this.t('common_next'), async () => {
          step = 4;
          await this.createMemberAndInvite(memberName, role, memberEmail, memberAge, enabledModules, parentalControls, modal, overlay);
        }));
        modal.appendChild(nav);

      }
    };

    renderStep();
    overlay.appendChild(modal);
    this.container.appendChild(overlay);
  }

  private async createMemberAndInvite(
    name: string, role: 'child' | 'member' | 'co-admin', email: string, age: string,
    enabledModules: string[], parentalControls: boolean,
    modal: HTMLElement, overlay: HTMLElement
  ): Promise<void> {
    modal.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;padding:40px;"><div class="aweni-spinner"></div></div>`;

    try {
      // Create member
      const payload: CreateMemberPayload = {
        name: name.trim(),
        role,
        enabledModules,
        parentalControls,
      };
      if (email.trim()) payload.email = email.trim();
      if (age && role === 'child') payload.age = parseInt(age, 10);

      await this.familyService.createMember(payload);

      // Generate invite
      const invite = await this.familyService.createInvite({
        type: role,
        enabledModules,
        parentalControls,
      });

      // Step 4: Show invite
      this.renderInviteStep(modal, overlay, invite, name);
    } catch {
      this.ctx.showNotification(this.t('common_error'), 'error');
      overlay.remove();
    }
  }

  private renderInviteStep(modal: HTMLElement, overlay: HTMLElement, invite: InviteLink, memberName: string): void {
    modal.innerHTML = '';

    const title = this.el('h2', { style: 'font-size:20px;font-weight:700;margin:0 0 8px;color:var(--text-primary);text-align:center;' });
    title.textContent = this.t('family_invite_ready');

    const desc = this.el('p', { style: 'font-size:13px;color:var(--text-secondary);margin:0 0 20px;text-align:center;' });
    desc.textContent = this.t('family_invite_share_with', { name: memberName });

    modal.append(title, desc);

    // Invite link box
    const linkBox = this.el('div', {
      style: 'background:var(--bg-secondary);border-radius:12px;padding:14px;margin-bottom:16px;text-align:center;'
    });
    const linkUrl = this.familyService.generateInviteUrl(invite.code);
    const linkText = this.el('div', {
      style: 'font-size:13px;font-family:monospace;color:var(--text-primary);word-break:break-all;margin-bottom:8px;'
    });
    linkText.textContent = linkUrl;

    const copyBtn = this.el('button', {
      style: 'padding:8px 20px;background:#0A0A0A;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:600;'
    });
    copyBtn.textContent = this.t('family_copy_link');
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(linkUrl);
        copyBtn.textContent = '\u2713 ' + this.t('family_copied');
        setTimeout(() => { copyBtn.textContent = this.t('family_copy_link'); }, 2000);
      } catch {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = linkUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
        copyBtn.textContent = '\u2713 ' + this.t('family_copied');
        setTimeout(() => { copyBtn.textContent = this.t('family_copy_link'); }, 2000);
      }
    });
    linkBox.append(linkText, copyBtn);
    modal.appendChild(linkBox);

    // Share buttons
    const shareRow = this.el('div', { style: 'display:flex;gap:8px;margin-bottom:16px;' });

    const waBtn = this.el('a', {
      href: this.familyService.generateWhatsAppShareUrl(invite.code, this.lang),
      target: '_blank',
      rel: 'noopener',
      style: 'flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:10px;background:#25D366;color:#fff;border-radius:10px;text-decoration:none;font-size:13px;font-weight:600;'
    });
    waBtn.textContent = this.t('family_share_whatsapp');
    shareRow.appendChild(waBtn);

    const emailBtn = this.el('a', {
      href: this.familyService.generateEmailShareUrl(invite.code, this.lang),
      style: 'flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:10px;background:#3B82F6;color:#fff;border-radius:10px;text-decoration:none;font-size:13px;font-weight:600;'
    });
    emailBtn.textContent = this.t('family_share_email');
    shareRow.appendChild(emailBtn);

    modal.appendChild(shareRow);

    // QR Code
    const qrContainer = this.el('div', { style: 'text-align:center;margin-bottom:16px;' });
    this.familyService.getInviteQR(invite.code).then(svg => {
      qrContainer.innerHTML = svg;
    });
    modal.appendChild(qrContainer);

    // Expiry notice
    const expiry = this.el('p', { style: 'font-size:12px;color:var(--text-secondary);text-align:center;margin:0 0 16px;' });
    expiry.textContent = this.t('family_invite_expires_in', { days: '7' });
    modal.appendChild(expiry);

    // Done button
    const done = this.createPrimaryButton(this.t('family_done'), async () => {
      overlay.remove();
      await this.loadAndRender();
    });
    modal.appendChild(done);
  }

  // ---- C. Member Detail View ----

  private showMemberDetail(member: FamilyMember): void {
    const overlay = this.createOverlay();
    const modal = this.el('div', {
      style: 'background:var(--bg-primary);border-radius:20px 20px 0 0;width:100%;max-width:480px;max-height:85vh;overflow-y:auto;padding:24px 20px 32px;',
      role: 'dialog',
      'aria-label': member.name
    });

    // Close
    const closeBtn = this.el('button', {
      style: `position:absolute;top:16px;${this.isRTL ? 'left' : 'right'}:16px;width:32px;height:32px;border-radius:50%;background:var(--bg-secondary);border:none;cursor:pointer;font-size:18px;color:var(--text-secondary);display:flex;align-items:center;justify-content:center;`,
      'aria-label': this.t('common_close')
    });
    closeBtn.textContent = '\u00d7';
    closeBtn.addEventListener('click', () => overlay.remove());

    // Avatar + name
    const profileHeader = this.el('div', { style: 'text-align:center;margin-bottom:20px;position:relative;' });
    profileHeader.appendChild(closeBtn);
    const avatar = this.el('div', {
      style: 'width:72px;height:72px;border-radius:50%;background:linear-gradient(135deg,#0A0A0A,#3B82F6);color:#fff;display:flex;align-items:center;justify-content:center;font-size:30px;font-weight:700;margin:0 auto 10px;'
    });
    avatar.textContent = member.avatar || member.name.charAt(0).toUpperCase();
    const nameEl = this.el('h2', { style: 'font-size:20px;font-weight:700;color:var(--text-primary);margin:0;' });
    nameEl.textContent = member.name;
    const roleEl = this.el('span', {
      style: `display:inline-block;margin-top:6px;padding:3px 12px;border-radius:12px;font-size:12px;font-weight:500;${this.getRoleBadgeStyle(member.role)}`
    });
    roleEl.textContent = this.t('family_role_' + member.role);
    const joined = this.el('div', { style: 'font-size:12px;color:var(--text-secondary);margin-top:6px;' });
    joined.textContent = `${this.t('family_joined')} ${new Date(member.createdAt).toLocaleDateString()} | ${this.t('family_last_active')}: ${member.lastActive ? this.formatRelativeTime(member.lastActive) : this.t('family_never')}`;
    profileHeader.append(avatar, nameEl, roleEl, joined);
    modal.appendChild(profileHeader);

    // Activity placeholder (weekly)
    const actSection = this.el('div', {
      style: 'background:var(--bg-secondary);border-radius:14px;padding:16px;margin-bottom:16px;'
    });
    const actTitle = this.el('h3', { style: 'font-size:15px;font-weight:600;color:var(--text-primary);margin:0 0 12px;' });
    actTitle.textContent = this.t('family_activity_this_week');
    actSection.appendChild(actTitle);

    // Simple bar chart placeholder
    const bars = this.el('div', { style: 'display:flex;align-items:flex-end;gap:6px;height:60px;' });
    const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    for (const d of days) {
      const barWrap = this.el('div', { style: 'flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;' });
      const bar = this.el('div', {
        style: `width:100%;height:${Math.floor(Math.random() * 40 + 10)}px;background:linear-gradient(180deg,#0A0A0A,#2A2520);border-radius:4px 4px 0 0;`
      });
      const label = this.el('span', { style: 'font-size:10px;color:var(--text-secondary);' });
      label.textContent = d;
      barWrap.append(bar, label);
      bars.appendChild(barWrap);
    }
    actSection.appendChild(bars);
    modal.appendChild(actSection);

    // Module access toggles
    if (this.familyService.canManageMembers({ role: 'admin' } as FamilyMember) && member.role !== 'admin') {
      const modSection = this.el('div', {
        style: 'background:var(--bg-secondary);border-radius:14px;padding:16px;margin-bottom:16px;'
      });
      const modTitle = this.el('h3', { style: 'font-size:15px;font-weight:600;color:var(--text-primary);margin:0 0 12px;' });
      modTitle.textContent = this.t('family_module_access');
      modSection.appendChild(modTitle);

      const allMods = this.familyService.getAllModules();
      const currentModules = member.enabledModules || this.familyService.getDefaultEnabledModules();

      for (const modId of allMods) {
        if (modId === 'home') continue;
        const row = this.el('div', {
          style: 'display:flex;align-items:center;justify-content:space-between;padding:6px 0;'
        });
        const lbl = this.el('span', { style: 'font-size:13px;color:var(--text-primary);text-transform:capitalize;' });
        lbl.textContent = modId;
        const toggle = this.createToggle(currentModules.includes(modId), async (on) => {
          const updated = on
            ? [...currentModules, modId]
            : currentModules.filter((m: string) => m !== modId);
          try {
            await this.familyService.updateMember(member.profileId, { enabledModules: updated });
            this.ctx.showNotification(this.t('common_save'), 'success');
          } catch { /* silent */ }
        });
        row.append(lbl, toggle);
        modSection.appendChild(row);
      }
      modal.appendChild(modSection);
    }

    // Actions
    if (member.role !== 'admin') {
      const actions = this.el('div', {
        style: 'display:flex;flex-direction:column;gap:8px;margin-top:8px;'
      });

      if (member.role === 'member') {
        const promoteBtn = this.createSecondaryButton(this.t('family_promote'), async () => {
          try {
            await this.familyService.promoteToCoadmin(member.profileId);
            this.ctx.showNotification(this.t('family_promoted'), 'success');
            overlay.remove();
            await this.loadAndRender();
          } catch { this.ctx.showNotification(this.t('common_error'), 'error'); }
        });
        actions.appendChild(promoteBtn);
      }

      if (member.role === 'co-admin') {
        const demoteBtn = this.createSecondaryButton(this.t('family_demote'), async () => {
          try {
            await this.familyService.demoteFromCoadmin(member.profileId);
            this.ctx.showNotification(this.t('family_demoted'), 'success');
            overlay.remove();
            await this.loadAndRender();
          } catch { this.ctx.showNotification(this.t('common_error'), 'error'); }
        });
        actions.appendChild(demoteBtn);
      }

      const suspendBtn = this.el('button', {
        style: 'padding:10px;background:#FEF3C7;color:#92400E;border:none;border-radius:10px;cursor:pointer;font-size:13px;font-weight:600;width:100%;'
      });
      suspendBtn.textContent = member.status === 'suspended' ? this.t('family_reactivate') : this.t('family_suspend');
      suspendBtn.addEventListener('click', async () => {
        try {
          if (member.status === 'suspended') {
            await this.familyService.reactivateMember(member.profileId);
          } else {
            await this.familyService.suspendMember(member.profileId);
          }
          this.ctx.showNotification(this.t('common_save'), 'success');
          overlay.remove();
          await this.loadAndRender();
        } catch { this.ctx.showNotification(this.t('common_error'), 'error'); }
      });
      actions.appendChild(suspendBtn);

      const removeBtn = this.el('button', {
        style: 'padding:10px;background:#FEE2E2;color:#DC2626;border:none;border-radius:10px;cursor:pointer;font-size:13px;font-weight:600;width:100%;'
      });
      removeBtn.textContent = this.t('family_remove');
      removeBtn.addEventListener('click', async () => {
        if (!confirm(this.t('family_remove_confirm', { name: member.name }))) return;
        try {
          await this.familyService.removeMember(member.profileId);
          this.ctx.showNotification(this.t('family_removed'), 'success');
          overlay.remove();
          await this.loadAndRender();
        } catch { this.ctx.showNotification(this.t('common_error'), 'error'); }
      });
      actions.appendChild(removeBtn);

      modal.appendChild(actions);
    }

    overlay.appendChild(modal);
    this.container.appendChild(overlay);
  }

  // ---- Empty state ----

  private renderEmpty(): void {
    this.container.innerHTML = '';
    const wrapper = this.el('div', {
      style: 'max-width:480px;margin:0 auto;padding:40px 20px;text-align:center;'
    });
    const icon = this.el('div', { style: 'font-size:48px;margin-bottom:16px;' });
    icon.textContent = '\ud83d\udc68\u200d\ud83d\udc69\u200d\ud83d\udc67\u200d\ud83d\udc66';
    const title = this.el('h2', { style: 'font-size:20px;font-weight:700;color:var(--text-primary);margin:0 0 8px;' });
    title.textContent = this.t('family_title');
    const desc = this.el('p', { style: 'font-size:14px;color:var(--text-secondary);margin:0 0 24px;' });
    desc.textContent = this.t('family_empty_desc');
    const addBtn = this.createPrimaryButton(this.t('family_add_member'), () => this.showAddMemberModal());
    wrapper.append(icon, title, desc, addBtn);
    this.container.appendChild(wrapper);
  }

  // ---- UI Helpers ----

  private el(tag: string, attrs?: Record<string, string>): HTMLElement {
    const el = document.createElement(tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (k === 'style') el.style.cssText = v;
        else el.setAttribute(k, v);
      }
    }
    return el;
  }

  private createOverlay(): HTMLElement {
    const overlay = this.el('div', {
      style: 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:1000;display:flex;align-items:flex-end;justify-content:center;',
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });
    return overlay;
  }

  private createPrimaryButton(text: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.style.cssText = 'width:100%;padding:12px;background:#0A0A0A;color:#fff;border:none;border-radius:12px;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;transition:background 0.2s;';
    btn.textContent = text;
    btn.addEventListener('mouseenter', () => { btn.style.background = '#2A2520'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = '#0A0A0A'; });
    btn.addEventListener('click', onClick);
    return btn;
  }

  private createSecondaryButton(text: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.style.cssText = 'flex:1;padding:10px;background:var(--bg-secondary);color:var(--text-primary);border:none;border-radius:10px;cursor:pointer;font-size:13px;font-weight:600;font-family:inherit;';
    btn.textContent = text;
    btn.addEventListener('click', onClick);
    return btn;
  }

  private createInput(label: string, value: string, onChange: (v: string) => void, type: string = 'text'): HTMLElement {
    const wrap = this.el('div');
    const lbl = this.el('label', { style: 'display:block;font-size:12px;font-weight:500;color:var(--text-secondary);margin-bottom:4px;' });
    lbl.textContent = label;
    const input = document.createElement('input');
    input.type = type;
    input.value = value;
    input.style.cssText = 'width:100%;padding:10px 12px;border:1.5px solid var(--bg-secondary);border-radius:10px;font-size:14px;font-family:inherit;background:var(--bg-primary);color:var(--text-primary);box-sizing:border-box;outline:none;transition:border-color 0.15s;';
    input.addEventListener('focus', () => { input.style.borderColor = '#0A0A0A'; });
    input.addEventListener('blur', () => { input.style.borderColor = 'var(--bg-secondary)'; });
    input.addEventListener('input', () => onChange(input.value));
    wrap.append(lbl, input);
    return wrap;
  }

  private createToggle(initialState: boolean, onChange: (on: boolean) => void): HTMLElement {
    let on = initialState;
    const toggle = this.el('div', {
      style: `width:44px;height:24px;border-radius:12px;background:${on ? '#0A0A0A' : '#d1d5db'};cursor:pointer;position:relative;transition:background 0.2s;`,
      role: 'switch',
      'aria-checked': String(on),
      tabindex: '0'
    });
    const knob = this.el('div', {
      style: `width:20px;height:20px;border-radius:50%;background:#fff;position:absolute;top:2px;${on ? (this.isRTL ? 'right' : 'left') + ':22px' : (this.isRTL ? 'right' : 'left') + ':2px'};transition:all 0.2s;box-shadow:0 1px 3px rgba(0,0,0,0.2);`
    });
    toggle.appendChild(knob);

    const doToggle = () => {
      on = !on;
      toggle.style.background = on ? '#0A0A0A' : '#d1d5db';
      toggle.setAttribute('aria-checked', String(on));
      const pos = this.isRTL ? 'right' : 'left';
      knob.style[pos as 'left'] = on ? '22px' : '2px';
      onChange(on);
    };

    toggle.addEventListener('click', doToggle);
    toggle.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); doToggle(); } });
    return toggle;
  }

  private formatRelativeTime(isoDate: string): string {
    const diff = Date.now() - new Date(isoDate).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return this.t('time_just_now');
    if (mins < 60) return this.t('time_minutes_ago', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return this.t('time_hours_ago', { count: hours });
    const days = Math.floor(hours / 24);
    return this.t('time_days_ago', { count: days });
  }
}
